import {
  Calculator,
  FileSpreadsheet,
  Gauge,
  Headset,
  MessagesSquare,
  PhoneMissed,
  Plus,
  ShieldCheck,
  Shuffle,
  Tag,
  TriangleAlert,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  customScenarioApi,
  scenarioApi,
  sourcesApi,
  type CustomScenario,
  type ScenarioCatalogue,
  type ScenarioIcon,
} from "../../services/api";
import { domainLabel, selectionFor } from "../../services/dataDomain";
import type { SelectedContext } from "../../services/storage";
import { ScenarioWorkbench } from "./ScenarioWorkbench";
import { SingleMetricCalculator } from "./SingleMetricCalculator";
import type { PinnedScenario } from "./components/PinnedScenarios";
import { CustomScenarioBuilder } from "./custom/CustomScenarioBuilder";
import {
  ScenarioFileError,
  fitSavedScenario,
  readScenarioWorkbook,
  type ScenarioInputs,
} from "./scenarioFile";

/** A scenario opened from a file. The serial remounts the workbench on each open. */
interface OpenedScenario {
  templateId: string;
  inputs: ScenarioInputs;
  serial: number;
}

interface FileNotice {
  tone: "error" | "warning" | "info";
  text: string;
}

/** The custom scenario builder, open on a new scenario or on one being edited. */
type Builder = { mode: "create" } | { mode: "edit"; scenario: CustomScenario };

interface WhatIfPageProps {
  selectedContext: SelectedContext | null;
  onSelectContext: (context: SelectedContext) => void;
}

/** The calculator every domain has, alongside any domain scenarios. */
export const SINGLE_METRIC = "single_metric";

const ICONS: Record<ScenarioIcon, LucideIcon> = {
  gauge: Gauge,
  tag: Tag,
  users: Users,
  "user-plus": UserPlus,
  calculator: Calculator,
  headset: Headset,
  "phone-missed": PhoneMissed,
  shuffle: Shuffle,
  messages: MessagesSquare,
};

function catalogueKey(contextId?: string, contextVersion?: string | null): string {
  return `${contextId ?? ""}@${contextVersion ?? ""}`;
}

function withoutPins(
  pins: Record<string, PinnedScenario[]>,
  templateId: string,
): Record<string, PinnedScenario[]> {
  const next = { ...pins };
  delete next[templateId];
  return next;
}

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * The What-If Studio adapts to the selected assistant's data domain. A domain
 * with a studio, such as Corporate Finance, opens on its executive scenarios;
 * every domain keeps the single-metric calculator. The source is chosen with
 * the assistant, not here, so the studio always matches the conversation.
 */
export function WhatIfPage({ selectedContext, onSelectContext }: WhatIfPageProps) {
  const [catalogue, setCatalogue] = useState<ScenarioCatalogue | null>(null);
  // The context the catalogue (or its failure) belongs to. Deriving loading
  // from it means nothing renders against a previous source's scenarios, and
  // the calculator does not mount, unmount and refetch while it resolves.
  const [resolvedFor, setResolvedFor] = useState<string | null>(null);
  const [catalogueError, setCatalogueError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [mode, setMode] = useState(SINGLE_METRIC);
  const [pins, setPins] = useState<Record<string, PinnedScenario[]>>({});
  const [opened, setOpened] = useState<OpenedScenario | null>(null);
  const [fileNotice, setFileNotice] = useState<FileNotice | null>(null);
  const [builder, setBuilder] = useState<Builder | null>(null);
  // Bumped to re-read the catalogue after a custom scenario changes.
  const [reload, setReload] = useState(0);
  // The scenario to open once the re-read catalogue arrives.
  const preferred = useRef<string | null>(null);

  useEffect(() => {
    // Only needed when nothing is selected yet: fall back to the first
    // queryable source so the page never opens empty.
    if (selectedContext) return;
    let active = true;
    sourcesApi
      .list()
      .then((items) => {
        if (!active) return;
        const first = items.find((item) => item.queryable);
        if (first) onSelectContext(selectionFor(first));
      })
      .catch((caught) => {
        if (active) setSourceError(message(caught, "Data sources could not be loaded."));
      });
    return () => {
      active = false;
    };
  }, [onSelectContext, selectedContext]);

  const contextId = selectedContext?.contextId;
  const contextVersion = selectedContext?.contextVersion;

  useEffect(() => {
    setCatalogue(null);
    setPins({});
    setOpened(null);
    setFileNotice(null);
    setBuilder(null);
    setMode(SINGLE_METRIC);
  }, [contextId, contextVersion]);

  useEffect(() => {
    let active = true;
    setCatalogueError("");
    if (!contextId) {
      return () => {
        active = false;
      };
    }
    scenarioApi
      .catalogue(contextId, contextVersion)
      .then((next) => {
        if (!active) return;
        setCatalogue(next);
        const wanted = preferred.current;
        preferred.current = null;
        const runnable = (id: string) =>
          next.templates.some((item) => item.id === id && item.available);
        setMode((current) => {
          if (wanted && runnable(wanted)) return wanted;
          // A re-read keeps the scenario in view unless it went away.
          if (current !== SINGLE_METRIC && runnable(current)) return current;
          return next.templates.find((item) => item.available)?.id ?? SINGLE_METRIC;
        });
      })
      .catch((caught) => {
        // The calculator still works without domain scenarios, so this is a
        // notice rather than a page failure.
        if (active) {
          setCatalogueError(
            message(caught, "Domain scenarios are unavailable right now."),
          );
        }
      })
      .finally(() => {
        if (active) setResolvedFor(catalogueKey(contextId, contextVersion));
      });
    return () => {
      active = false;
    };
  }, [contextId, contextVersion, reload]);

  const loadingCatalogue =
    Boolean(contextId) && resolvedFor !== catalogueKey(contextId, contextVersion);

  const templates = catalogue?.templates ?? [];
  const active = templates.find((item) => item.id === mode && item.available);
  const audience = domainLabel(catalogue?.dataDomain ?? selectedContext?.dataDomain);
  const hasStudio = templates.length > 0;

  function choose(nextMode: string) {
    setOpened(null);
    setFileNotice(null);
    setBuilder(null);
    setMode(nextMode);
  }

  async function edit(scenarioId: string) {
    setFileNotice(null);
    try {
      setBuilder({ mode: "edit", scenario: await customScenarioApi.get(scenarioId) });
    } catch (caught) {
      setFileNotice({ tone: "error", text: message(caught, "The scenario could not be opened.") });
    }
  }

  async function remove(scenarioId: string, title: string) {
    if (!window.confirm(`Delete "${title}"? Only your copy exists, so this cannot be undone.`)) {
      return;
    }
    try {
      await customScenarioApi.remove(scenarioId);
      setPins((current) => withoutPins(current, scenarioId));
      setMode(SINGLE_METRIC);
      setFileNotice({ tone: "info", text: `Deleted ${title}.` });
      setReload((value) => value + 1);
    } catch (caught) {
      setFileNotice({ tone: "error", text: message(caught, "The scenario could not be deleted.") });
    }
  }

  function saved(scenario: CustomScenario) {
    const editing = builder?.mode === "edit";
    setBuilder(null);
    // An edited formula changes every figure, so earlier pins no longer compare.
    if (editing) setPins((current) => withoutPins(current, scenario.id));
    preferred.current = scenario.id;
    setFileNotice({
      tone: "info",
      text: `${editing ? "Saved changes to" : "Saved"} ${scenario.title}. Only you can see it.`,
    });
    setReload((value) => value + 1);
  }

  async function openScenario(file: File) {
    setFileNotice(null);
    try {
      const saved = readScenarioWorkbook(new Uint8Array(await file.arrayBuffer()));
      const template = templates.find((item) => item.id === saved.templateId);
      if (!template) {
        throw new ScenarioFileError(
          `"${saved.templateTitle}" is not a scenario offered for ${selectedContext?.name ?? "this source"}.`,
        );
      }
      if (!template.available) {
        throw new ScenarioFileError(
          `${template.title} is unavailable for this source${template.unavailableReason ? `: ${template.unavailableReason}` : "."}`,
        );
      }
      const { inputs, adjusted } = fitSavedScenario(template, saved);
      setMode(template.id);
      setOpened((previous) => ({
        templateId: template.id,
        inputs,
        serial: (previous?.serial ?? 0) + 1,
      }));
      const notes = [`Opened ${file.name}. The scenario is recalculated on today's governed baseline.`];
      if (saved.contextId && saved.contextId !== contextId) {
        notes.push(`It was saved against ${saved.contextId}.`);
      }
      if (adjusted.length > 0) {
        notes.push(`To fit the current scenario, ${adjusted.join("; ")}.`);
      }
      setFileNotice({ tone: adjusted.length > 0 ? "warning" : "info", text: notes.join(" ") });
    } catch (caught) {
      setFileNotice({
        tone: "error",
        text:
          caught instanceof ScenarioFileError
            ? caught.message
            : "The file could not be opened as a saved scenario.",
      });
    }
  }

  return (
    <div className="content-stack what-if-page">
      <section className="panel scenario-intro">
        <div>
          <span className="eyebrow">
            {audience ? `${audience} · What-If Studio` : "What-If Studio"}
          </span>
          <h2>
            {hasStudio
              ? catalogue?.studioTitle
              : "Test one change against governed actuals"}
          </h2>
          <p>
            {hasStudio
              ? catalogue?.studioSummary
              : "Choose a certified metric and apply one percentage adjustment. Nothing is written back to the source."}
          </p>
        </div>
        <div className="scenario-intro-side">
          {selectedContext?.name && (
            <span className="scenario-source" title={selectedContext.contextId}>
              {selectedContext.name}
            </span>
          )}
          <span className="status-badge certified">
            <ShieldCheck size={14} /> Governed baseline
          </span>
        </div>
      </section>

      {(sourceError || catalogueError) && (
        <div className="scenario-warning" role="status">
          <TriangleAlert size={16} />
          <span>{sourceError || catalogueError}</span>
        </div>
      )}

      {contextId && (
        <nav className="scenario-templates" aria-label="Scenarios">
          {templates.map((item) => {
            const Icon = ICONS[item.icon] ?? Calculator;
            const selected = !builder && mode === item.id;
            // A custom scenario that can no longer run opens in the builder,
            // which is where its owner can fix it.
            const repairable = Boolean(item.custom) && !item.available;
            return (
              <button
                type="button"
                key={item.id}
                className={`scenario-template${selected ? " active" : ""}${item.custom ? " custom" : ""}`}
                aria-pressed={selected}
                disabled={!item.available && !repairable}
                title={item.unavailableReason ?? item.summary}
                onClick={() => (repairable ? void edit(item.id) : choose(item.id))}
              >
                <Icon size={18} aria-hidden="true" />
                <strong>
                  {item.title}
                  {item.custom && <em className="scenario-template-badge">Yours</em>}
                </strong>
                <span>
                  {repairable ? `Needs attention: ${item.unavailableReason}` : item.question}
                </span>
              </button>
            );
          })}
          {!loadingCatalogue && (
            <button
              type="button"
              className={`scenario-template${!builder && mode === SINGLE_METRIC ? " active" : ""}`}
              aria-pressed={!builder && mode === SINGLE_METRIC}
              onClick={() => choose(SINGLE_METRIC)}
            >
              <Calculator size={18} aria-hidden="true" />
              <strong>Single metric</strong>
              <span>Change any certified metric by a percentage</span>
            </button>
          )}
          {!loadingCatalogue && (
            <button
              type="button"
              className={`scenario-template scenario-template-create${builder?.mode === "create" ? " active" : ""}`}
              aria-pressed={builder?.mode === "create"}
              onClick={() => {
                setFileNotice(null);
                setBuilder({ mode: "create" });
              }}
            >
              <Plus size={18} aria-hidden="true" />
              <strong>Custom scenario</strong>
              <span>Build your own from certified metrics and formulas</span>
            </button>
          )}
          {loadingCatalogue && (
            <span className="scenario-templates-loading">
              <span className="button-spinner" /> Loading scenarios…
            </span>
          )}
        </nav>
      )}

      {fileNotice && (
        <div
          className={fileNotice.tone === "error" ? "scenario-error" : "scenario-warning"}
          role={fileNotice.tone === "error" ? "alert" : "status"}
        >
          {fileNotice.tone === "info" ? <FileSpreadsheet size={16} /> : <TriangleAlert size={16} />}
          <span>{fileNotice.text}</span>
        </div>
      )}

      {builder && contextId ? (
        <CustomScenarioBuilder
          key={builder.mode === "edit" ? builder.scenario.id : "new"}
          contextId={contextId}
          contextVersion={catalogue?.contextVersion ?? contextVersion}
          existing={builder.mode === "edit" ? builder.scenario : undefined}
          onSaved={saved}
          onCancel={() => setBuilder(null)}
        />
      ) : active && contextId ? (
        <ScenarioWorkbench
          // The reload count remounts it after an edit, which can change its inputs.
          key={`${contextId}:${active.id}:${reload}:${opened?.templateId === active.id ? opened.serial : 0}`}
          template={active}
          contextId={contextId}
          contextVersion={catalogue?.contextVersion ?? contextVersion}
          pins={pins[active.id] ?? []}
          onPinsChange={(next) =>
            setPins((previous) => ({ ...previous, [active.id]: next }))
          }
          initial={opened?.templateId === active.id ? opened.inputs : undefined}
          onOpen={(file) => void openScenario(file)}
          onEdit={active.custom ? () => void edit(active.id) : undefined}
          onDelete={active.custom ? () => void remove(active.id, active.title) : undefined}
        />
      ) : (
        !loadingCatalogue && <SingleMetricCalculator selectedContext={selectedContext} />
      )}
    </div>
  );
}
